import { parsePriceDecimalPlaces } from '@/utils/currency'
import { addEndpointPrefix } from '@/utils/apiAdapter'
import { OrderSource } from '@/view-models/order'
import { useApiClient } from '@/composables/useApiClient'
import { RemarkInputType } from '@/view-models/remark'

const getDefaultState = () => {
  return {
    headCount: 0,
    productCount: 1,
    bookingStartDateTime: '',
    bookingEndDateTime: '',
    bookingCheckoutDate: '',
    bookingCrossDate: [],
    productMultiDayAmount: 0,
    bookingDiscount: 0,
    bookingTotalAmount: 0,
    bookingDateArray: [],
    bookingSeat: null,
    upgradeResource: { items: [], totalAmount: 0 },
    additionResource: { items: [], totalAmount: 0 },
    upgradesMap: {},
    additionsMap: {},
    productsForSubitem: [],
    productsSubitems: [],
    remarksItems: [],
    remarkTagsItems: [],
    memberNote: '',
    funbookV2Form: {
      saveToBrowser: false,
      branchMarket: 1,
      funnowMarket: 1,
    },
    /** [remarkId]: string (content) */
    remarkFormData: {},
    /** [remarkId]: number (optionId a.k.a. sub_id) */
    remarkFormOptionData: {},
    timeZone: 'Asia/Taipei',
  }
}

export const state = () => getDefaultState()

export const getters = {
  bookingTotalAmount: state => state.bookingTotalAmount,
  headCount: state => state.headCount,
  productCount: state => state.productCount,
  timeZone: state => state.timeZone,
  bookingStartDateTime: state => state.bookingStartDateTime,
  bookingEndDateTime: state => state.bookingEndDateTime,
  bookingCheckoutDate: state => state.bookingCheckoutDate,
  bookingCrossDate: state => state.bookingCrossDate,
  productMultiDayAmount: state => state.productMultiDayAmount,
  bookingDiscount: state => state.bookingDiscount,
  bookingDateArray: state => state.bookingDateArray,
  upgradesMap: state => state.upgradesMap,
  additionsMap: state => state.additionsMap,
  upgradeResource: state => state.upgradeResource,
  additionResource: state => state.additionResource,
  /** for 明細 modal 資料用 */
  externalResources: (state, getters, rootState, rootGetters) =>
    [].concat(rootState.isBookingEngine || state.bookingCrossDate.length ? [] : state.upgradeResource.items, state.additionResource.items),
  productsForSubitem: state => state.productsForSubitem,
  productsSubitems: state => state.productsSubitems,
  remarksItems: state => state.remarksItems,
  remarkTagsItems: state => state.remarkTagsItems,
  remarksArr: state =>
    [...(state.remarksItems || []), ...(state.remarkTagsItems || [])]
      ?.filter(item => (Array.isArray(state.remarkFormData[item.id]) ? state.remarkFormData[item.id].length > 0 : !!state.remarkFormData[item.id]))
      .map(item => {
        const isArrayField = Array.isArray(state.remarkFormData[item.id])
        const singleContent = isArrayField ? state.remarkFormData[item.id]?.[0]?.content?.toString() : state.remarkFormData[item.id]?.toString() || ''
        const singleSubId = state.remarkFormOptionData[item.id] || null
        return {
          remark_id: parseInt(item.id),
          type: item.type,
          content: singleContent,
          sub_id: singleSubId,
          answers: isArrayField ? state.remarkFormData[item.id] : [{ sub_id: singleSubId, content: singleContent }],
        }
      }) || [],
  remarkNote: state => state.remarksItems?.map(item => `${item.title}: ${state.remarkFormData[item.id]?.toString() || ''}`).join('\n') || '',
  totalProductCount: (state, getters, rootState, rootGetters) => {
    const allowMulti = rootState.product.productInfo.allow_multi
    const subitemType = rootState.product.productInfo.sub_item_type
    // 兩個值都要為真才用 productCount，如果只有 allow_multi 為真，但 sub_item_type 不為真，則用 upgradeResource 的數量計算
    if (allowMulti === 1 && subitemType === 1) {
      return state.productCount
    }

    const upgradeTotal = state.upgradeResource.items.reduce((accumulator, currentValue) => {
      return accumulator + (currentValue.quantity || 1)
    }, 0)
    return upgradeTotal || 1
  },
  orderEmail: (state, getters) => getters.remarksArr.find(remark => +remark.remark_id === 5)?.content || '',
  bookingOrder: (state, getters, rootState, rootGetters) => ({
    addition: state.additionResource.items,
    bookingtime: state.bookingStartDateTime,
    checkoutday: state.bookingCheckoutDate,
    product: { pid: (rootGetters['product/productInfo']?.pid || '').toString(), count: getters.totalProductCount },
    remark: getters.remarksArr,
    resource_count: rootState.groupOrderStatus ? 0 : state.headCount,
    upgrades: state.upgradeResource.items.filter(items => items.upgrade_id >= 0),
    source: OrderSource.FunNow,
    memberNote: rootGetters.isFromForeignDomain ? [state.memberNote, getters.remarkNote].filter(Boolean).join('\n') : state.memberNote,
    branch_mkt: state.funbookV2Form.branchMarket,
    funnow_mkt: state.funbookV2Form.funnowMarket,
    seat: state.bookingSeat ?? null,
  }),
}

export const actions = {
  resetBookingState({ commit }) {
    commit('RESET_BOOKING_STATE')
  },
  setBookingAmount({ commit }, totalBookingAmount) {
    commit('SET_BOOKING_AMOUNT', totalBookingAmount)
  },
  setUpgradeAdditionSubitemBookingAmount({ state, commit }, precision) {
    const decimalPoint = precision || 0

    let productsSingleDaySubitemsAmount = 0
    state.productsForSubitem.forEach(product => {
      productsSingleDaySubitemsAmount = productsSingleDaySubitemsAmount + (product?.productSingleDaySubitemsAmount || 0)
    })
    let totalBookingAmount = 0
    const nights = state.bookingCrossDate.length || 1
    const singleProductPrice = state.bookingCrossDate.length > 0 ? state.productMultiDayAmount || 0 : state.bookingDiscount || 0
    const productsPrice = singleProductPrice * state.productCount
    totalBookingAmount += state.upgradeResource.totalAmount || productsPrice
    totalBookingAmount += productsSingleDaySubitemsAmount * nights
    totalBookingAmount += state.additionResource.totalAmount || 0
    commit('SET_BOOKING_AMOUNT', parsePriceDecimalPlaces(totalBookingAmount, decimalPoint))
  },
  setBookingHeadCount({ commit }, headCount) {
    commit('SET_BOOKING_HEAD_COUNT', headCount)
  },
  setBookingProductCount({ commit }, productCount) {
    commit('SET_BOOKING_PRODUCT_COUNT', productCount)
  },
  setBookingTimeZone({ commit }, timeZone) {
    commit('SET_BOOKING_TIME_ZONE', timeZone)
  },
  setBookingStartDateTime({ commit }, dateTime) {
    commit('SET_BOOKING_START_DATE_TIME', dateTime)
  },
  setBookingEndDateTime({ commit }, dateTime) {
    commit('SET_BOOKING_END_DATE_TIME', dateTime)
  },
  setBookingCheckoutDate({ commit }, date) {
    commit('SET_BOOKING_CHECKOUT_DATE', date)
  },
  setBookingDateArray({ commit }, bookingDateArray) {
    commit('SET_BOOKING_DATE_ARRAY', bookingDateArray)
  },
  setBookingCrossDate({ commit }, bookingCrossDate) {
    commit('SET_BOOKING_CROSS_DATE', bookingCrossDate)
  },
  setProductMultiDayAmount({ commit }, productMultiDayAmount) {
    commit('SET_PRODUCT_MULTI_DAY_AMOUNT', productMultiDayAmount)
  },
  setBookingDiscount({ commit }, discount) {
    commit('SET_BOOKING_DISCOUNT', discount)
  },
  setUpgradeResource({ dispatch, commit }, { upgradeResource, decimalPoint }) {
    commit('SET_UPGRADE_RESOURCE', upgradeResource)
    dispatch('setUpgradeAdditionSubitemBookingAmount', decimalPoint)
  },
  setAdditionResource({ dispatch, commit }, { additionResource, decimalPoint }) {
    commit('SET_ADDITION_RESOURCE', additionResource)
    dispatch('setUpgradeAdditionSubitemBookingAmount', decimalPoint)
  },
  setProductsForSubitem({ dispatch, commit }, { productsForSubitem, decimalPoint }) {
    commit('SET_PRODUCTS_FOR_SUBITEM', productsForSubitem)
    dispatch('setUpgradeAdditionSubitemBookingAmount', decimalPoint)
  },
  setProductsSubitems({ dispatch, commit }, productsSubitems) {
    commit('SET_PRODUCTS_SUBITEMS', productsSubitems)
  },
  resetResource({ dispatch, commit }) {
    const resetResource = { items: [], totalAmount: 0 }
    commit('SET_UPGRADE_RESOURCE', Object.assign({}, resetResource))
    commit('SET_ADDITION_RESOURCE', Object.assign({}, resetResource))
    dispatch('setUpgradeAdditionSubitemBookingAmount')
  },
  async getRemarks({ commit }, { pid }) {
    const apiUrl = `/pub/product/${pid}/remarks`
    const res = await useApiClient().$get(apiUrl, { headers: { APIVer: 2 } })
    if (res.code === 0) {
      const remarkItems = res.data || []
      const remarkTagsItems = remarkItems.filter(item => item.type === RemarkInputType.Tags)
      const normalRemarkItems = remarkItems.filter(item => item.type !== RemarkInputType.Tags)
      commit('SET_REMARKS_ITEMS', normalRemarkItems)
      commit('SET_REMARK_TAGS_ITEMS', remarkTagsItems)
    }
    return res
  },
  createBookingOrder({ commit }, orderData = {}) {
    const apiRul = `/order`
    return useApiClient()
      .$post(apiRul, orderData)
      .then(res => {
        return res
      })
      .catch(error => {
        return error
      })
  },
  async releaseOrder({ commit }, { orderId, token }) {
    const prefix = addEndpointPrefix(!!token)
    const apiUrl = `${prefix}/order/${orderId}/release`
    const data = token ? { token } : {}
    try {
      const res = await useApiClient().$delete(apiUrl, { data })

      return res.data
    } catch (error) {
      console.error(error)

      return error
    }
  },
}

export const mutations = {
  RESET_BOOKING_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  SET_PRODUCT_MULTI_DAY_AMOUNT(state, productMultiDayAmount) {
    state.productMultiDayAmount = productMultiDayAmount
  },
  SET_BOOKING_AMOUNT(state, bookingAmount) {
    state.bookingTotalAmount = bookingAmount
  },
  SET_BOOKING_HEAD_COUNT(state, headCount) {
    state.headCount = Number(headCount) || 1
  },
  SET_BOOKING_PRODUCT_COUNT(state, productCount) {
    state.productCount = productCount
  },
  SET_BOOKING_TIME_ZONE(state, timeZone) {
    state.timeZone = timeZone
  },
  SET_BOOKING_START_DATE_TIME(state, dateTime) {
    state.bookingStartDateTime = dateTime
  },
  SET_BOOKING_END_DATE_TIME(state, dateTime) {
    state.bookingEndDateTime = dateTime
  },
  SET_BOOKING_CHECKOUT_DATE(state, date) {
    state.bookingCheckoutDate = date
  },
  SET_BOOKING_DISCOUNT(state, discount) {
    state.bookingDiscount = discount
  },
  SET_BOOKING_DATE_ARRAY(state, bookingDateArray) {
    state.bookingDateArray = bookingDateArray
  },
  SET_UPGRADE_RESOURCE(state, upgradeResource) {
    state.upgradeResource = upgradeResource
  },
  SET_ADDITION_RESOURCE(state, additionResource) {
    state.additionResource = additionResource
  },
  SET_PRODUCTS_FOR_SUBITEM(state, productsForSubitem) {
    state.productsForSubitem = productsForSubitem
  },
  SET_PRODUCTS_SUBITEMS(state, productsSubitems) {
    state.productsSubitems = productsSubitems
  },
  SET_UPGRADE_MAP(state, upgrades = []) {
    const upgradesMap = {}
    upgrades.forEach(upgrad => {
      upgradesMap[upgrad.upgrade_id] = Object.assign({}, upgrad)
    })
    state.upgradesMap = upgradesMap
  },
  SET_ADDITION_MAP(state, additions = []) {
    const additionsMap = {}
    additions.forEach(addition => {
      additionsMap[addition.product_id] = addition
    })
    state.additionsMap = additionsMap
  },
  SET_REMARKS_ITEMS(state, remarksItems) {
    state.remarksItems = remarksItems
  },
  SET_REMARK_TAGS_ITEMS(state, remarkTagsItems) {
    state.remarkTagsItems = remarkTagsItems
  },
  SET_BOOKING_CROSS_DATE(state, bookingCrossDate) {
    state.bookingCrossDate = [].concat(bookingCrossDate)
  },
  UPDATE_ITEMS_OF_PRODUCTS_SUBITEMS(state, { subitems, index }) {
    state.productsSubitems = [...state.productsSubitems.slice(0, index), subitems, ...state.productsSubitems.slice(index + 1)]
  },
  SET_MEMBER_NOTE(state, memberNote = '') {
    state.memberNote = memberNote
  },
  SET_FUNBOOK_V2_BRANCH_MARKET(state, branchMarket = '') {
    state.funbookV2Form.branchMarket = branchMarket
  },
  SET_FUNBOOK_V2_FUNNOW_MARKET(state, funnowMarket = '') {
    state.funbookV2Form.funnowMarket = funnowMarket
  },
  SET_REMARK_FORM_DATA(state, remarkFormData) {
    state.remarkFormData = { ...remarkFormData }
  },
  SET_REMARK_FORM_OPTION_DATA(state, remarkFormOptionData) {
    state.remarkFormOptionData = { ...remarkFormOptionData }
  },
  SET_BOOKING_SEAT(state, seat) {
    state.bookingSeat = seat
  },
}
